import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ApprovalRequest } from '@hospasense/contracts';
import { APPROVAL_STATUSES } from '@hospasense/contracts';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useApprovalMutation, useApprovals } from '../lib/queries-s2.js';
import { formatDateTime, relativeTime, titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  PermissionDeniedState,
  Select,
  StatusPill,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const STATUS_TONES = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'info',
  rejected: 'error',
  executed: 'success',
  expired: 'neutral',
  cancelled: 'neutral',
} as const;

/** Approval Inbox: request → two-person decision → requester execution. */
export function ApprovalsPage() {
  const { hasPermission, me } = useAuth();
  const [params, setParams] = useSearchParams();
  const allowed = hasPermission('approval.read');
  const query = useApprovals(params, allowed);

  if (!allowed) return <PermissionDeniedState what="the Approval Inbox" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Approval Inbox"
        subtitle="Separation of duties is enforced: you can never approve your own request, and only the requester executes"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Approvals' }]} />
        }
      />
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4">
        <Select
          label="Status"
          value={params.get('status') ?? ''}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('status', e.target.value);
            else next.delete('status');
            setParams(next, { replace: true });
          }}
        >
          <option value="">All statuses</option>
          {APPROVAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {titleCase(s)}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 pb-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={params.get('mine') === 'true'}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.checked) next.set('mine', 'true');
              else next.delete('mine');
              setParams(next, { replace: true });
            }}
            className="size-4 accent-(--primary)"
          />
          My requests only
        </label>
      </div>

      {query.isLoading ? (
        <LoadingState label="Loading approvals…" />
      ) : query.isError || !query.data ? (
        <ErrorState
          description="Approvals could not be loaded."
          onRetry={() => void query.refetch()}
        />
      ) : query.data.items.length === 0 ? (
        <Card>
          <EmptyState
            title="No approval requests"
            description="Waivers, go-live requests and export authorisations appear here."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {query.data.items.map((request) => (
            <ApprovalCard key={request.id} request={request} myUserId={me?.userId ?? ''} />
          ))}
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ request, myUserId }: { request: ApprovalRequest; myUserId: string }) {
  const { hasPermission } = useAuth();
  const mutations = useApprovalMutation();
  const toast = useToast();
  const [decisionOpen, setDecisionOpen] = useState<'approve' | 'reject' | null>(null);
  const [note, setNote] = useState('');

  const isMine = request.requestedByUserId === myUserId;
  const canDecide = request.status === 'submitted' && hasPermission('approval.approve') && !isMine;
  const canExecute = request.status === 'approved' && isMine;
  const canCancel = isMine && ['draft', 'submitted', 'approved'].includes(request.status);

  const err = (e: unknown, fallback: string): void =>
    toast.error(e instanceof ApiError ? e.message : fallback);

  return (
    <Card>
      <CardHeader
        title={request.actionLabel}
        subtitle={`${request.requestedByDisplayName ?? '—'} · ${relativeTime(request.createdAt)} · ${
          request.organisationName ?? 'platform-wide'
        } · ${request.policy === 'two_person' ? 'two-person control' : 'single approver'}`}
        actions={
          <StatusPill tone={STATUS_TONES[request.status]} label={titleCase(request.status)} />
        }
      />
      <CardBody className="space-y-3">
        <p className="text-sm text-text-secondary">{request.reason}</p>
        {request.decisionNote && (
          <p className="rounded-lg bg-bg-subtle px-3 py-2 text-xs text-text-secondary">
            <span className="font-medium">{request.decidedByDisplayName ?? '—'}</span> ·{' '}
            {formatDateTime(request.decidedAt)}: {request.decisionNote}
          </p>
        )}
        {request.status === 'approved' && request.approvalExpiresAt && (
          <p className="text-xs text-warning">
            Approval expires {formatDateTime(request.approvalExpiresAt)} — execution after that is
            refused.
          </p>
        )}
        {request.executionResult && (
          <p className="text-xs text-success">Executed: {request.executionResult}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {canDecide && (
            <>
              <Button size="sm" onClick={() => setDecisionOpen('approve')}>
                Approve
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDecisionOpen('reject')}>
                Reject
              </Button>
            </>
          )}
          {request.status === 'submitted' && isMine && (
            <Badge tone="info">Waiting for another operator to decide</Badge>
          )}
          {canExecute && (
            <Button
              size="sm"
              loading={mutations.execute.isPending}
              onClick={() =>
                void mutations.execute
                  .mutateAsync({ id: request.id, expectedVersion: request.version })
                  .then(({ result }) => toast.success(result))
                  .catch((e) => err(e, 'Execution failed'))
              }
            >
              Execute
            </Button>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void mutations.cancel
                  .mutateAsync({ id: request.id, expectedVersion: request.version })
                  .then(() => toast.success('Request cancelled'))
                  .catch((e) => err(e, 'Cancel failed'))
              }
            >
              Cancel
            </Button>
          )}
        </div>
      </CardBody>

      <Modal
        open={decisionOpen !== null}
        onClose={() => setDecisionOpen(null)}
        title={`${decisionOpen === 'approve' ? 'Approve' : 'Reject'}: ${request.actionLabel}`}
        footer={
          <Button
            variant={decisionOpen === 'reject' ? 'danger' : 'primary'}
            loading={mutations.decide.isPending}
            disabled={note.trim().length < 3}
            onClick={() =>
              void mutations.decide
                .mutateAsync({
                  id: request.id,
                  decision: decisionOpen!,
                  note,
                  expectedVersion: request.version,
                })
                .then(() => {
                  toast.success(`Request ${decisionOpen === 'approve' ? 'approved' : 'rejected'}`);
                  setDecisionOpen(null);
                  setNote('');
                })
                .catch((e) => err(e, 'Decision failed'))
            }
          >
            {decisionOpen === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        }
      >
        <Textarea
          label="Decision note"
          required
          hint="Recorded in the audit log and shown to the requester."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal>
    </Card>
  );
}
